import { Button, mediaQueries, style, Box } from '@do/walrus';
import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import {
  ButtonBackgroundProps,
  ButtonTextProps,
  SplashProps
} from '../interfaces/variant';
import { Column, WrapSplit } from './Common';

interface VariantCopyProps {
  color?: string;
  sizeMobile?: string;
  sizeDesktop?: string;
}
interface Props {
  splash: SplashProps;
}

const Wrap = styled.div<{ paddingX?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  z-index: ${style.vars.zIndex.base};
  text-align: left;
  width: 100%;
  max-width: 720px;
  margin-top: 70px;
  padding: 0 ${style.vars.whitespace.regular};
  ${mediaQueries.mediumMin} {
    margin-top: 115px;
    padding: ${({ paddingX }) => (paddingX ? `0 ${paddingX}` : '0 128px')};
  }
`;

const Title = styled.h2<VariantCopyProps>`
  && {
    white-space: pre-line;
    padding: 0;
    margin: ${style.vars.space['3']} 0;
    color: ${({ color }) => (color ? color : 'initial')};
    font-size: ${({ sizeMobile }) => (sizeMobile ? sizeMobile : '1.5rem')};
    ${mediaQueries.mediumMin} {
      font-size: ${({ sizeDesktop }) => (sizeDesktop ? sizeDesktop : '2rem')};
    }
  }
`;

const SubTitle = styled.h4<VariantCopyProps>`
  && {
    white-space: pre-line;
    padding: 0;
    margin-top: 0;
    color: ${({ color }) => (color ? color : 'initial')};
    font-size: ${({ sizeMobile }) => (sizeMobile ? sizeMobile : '1rem')};
    ${mediaQueries.mediumMin} {
      font-size: ${({ sizeDesktop }) =>
        sizeDesktop ? sizeDesktop : '1.125rem'};
    }
  }
`;

interface ButtonColorProps {
  buttonColor?: ButtonBackgroundProps;
  textColor?: ButtonTextProps;
}

const CustomSplashButton = styled(Button)<ButtonColorProps>`
  ${({ buttonColor, textColor }) =>
    buttonColor &&
    css`
      background: ${buttonColor.background};
      color: ${textColor?.color || 'inherit'};

      :hover,
      :focus {
        background: ${buttonColor.hoverBackground} !important;
        color: ${textColor?.hoverColor || 'inherit'};
      }
    `}
`;

type SplashButtonProps = React.ComponentProps<
  typeof Button & typeof CustomSplashButton
>;

const SplashButton = (props: SplashButtonProps): ReactElement => {
  const isMobile = mediaQueries.smallMax.useMatchesBreakpoint();
  return (
    <Box mt={style.vars.space['2']} mb={style.vars.space['10']}>
      <CustomSplashButton {...props} fullWidth={props.fullWidth || isMobile} />
    </Box>
  );
};

const ImgWrapper = styled.img<{ width?: string; margin?: string }>`
  width: ${({ width }) => width || '100%'};
  margin: ${({ margin }) => margin || '0'};
`;

export const Splash: React.FC<Props> = ({ splash }): ReactElement => (
  <Column
    style={{
      backgroundImage: `${
        splash.background.image ? `url(${splash.background.image})` : undefined
      }`,
      backgroundPosition: `${
        splash.background.horizontalPosition ?? 'center'
      } ${splash.background.verticalPosition ?? 'center'}`,
      backgroundColor: style.colors.primaryBackground,
      backgroundSize: 'cover'
    }}
  >
    <WrapSplit>
      {(splash.logo || splash.title) && (
        <Wrap paddingX={splash.paddingX}>
          {splash.logo && (
            <ImgWrapper
              data-testid="variantBackground"
              src={splash.logo.image}
              width={splash.logo.width}
              margin={splash.logo.margin}
            ></ImgWrapper>
          )}
          {splash.title && (
            <Title
              color={splash.title.color || splash.textColor}
              sizeDesktop={splash.title.sizeDesktop}
              sizeMobile={splash.title.sizeMobile}
            >
              {splash.title.text}
            </Title>
          )}
          {splash.subtitle && (
            <SubTitle
              color={splash.subtitle.color || splash.textColor}
              sizeDesktop={splash.subtitle.sizeDesktop}
              sizeMobile={splash.subtitle.sizeMobile}
            >
              {splash.subtitle.text}
            </SubTitle>
          )}
          {splash.button && (
            <SplashButton
              buttonColor={splash.button.buttonColor}
              variation={splash.button.variation || 'primary'}
              url={splash.button.url}
              fullWidth={splash.button.fullWidth}
              textColor={splash.button.textColor}
              external
            >
              {splash.button.text}
            </SplashButton>
          )}
        </Wrap>
      )}
    </WrapSplit>
  </Column>
);
